import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Camera, Image, Trash2, ArrowRight, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from './ChallengeShared';
import { UserAvatar } from '../UserAvatar';

export function TransformationsTab() {
  const { user } = useAuth();
  const [transformations, setTransformations] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const [tRes, pRes] = await Promise.allSettled([
        apiClient.get('/challenge/transformations'),
        apiClient.get('/challenge/mon-profil'),
      ]);
      if (tRes.status === 'fulfilled') setTransformations(tRes.value.data);
      if (pRes.status === 'fulfilled') setMyProfile(pRes.value.data.participant);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Spinner />;

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="transformations-tab">
      {/* My photos upload (if I'm a participant) */}
      {myProfile && myProfile.actif && (
        <MyPhotoSection profile={myProfile} onUpdate={fetchData} />
      )}

      {/* All transformations */}
      <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
        Transformations de la Meute
      </h2>

      {transformations.length === 0 ? (
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-6 text-center">
          <Camera className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">Aucune transformation pour le moment</p>
          <p className="text-zinc-600 text-xs mt-1">Les challengers peuvent ajouter leurs photos avant/apres</p>
        </div>
      ) : (
        <div className="space-y-4">
          {transformations.map(t => (
            <TransformationCard key={t.id} t={t} isAdmin={isAdmin} onUpdate={fetchData} />
          ))}
        </div>
      )}
    </div>
  );
}

function MyPhotoSection({ profile, onUpdate }) {
  return (
    <div className="bg-[#1B1B1B] border border-[#FF6B35]/30 rounded-lg p-4">
      <h3 className="text-base font-bold text-[#FF6B35] mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
        Ma Transformation
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <PhotoUploader
          label="Photo AVANT"
          currentPhoto={profile.photo_avant}
          pid={profile.id}
          type="avant"
          onUpdate={onUpdate}
        />
        <PhotoUploader
          label="Photo APRES"
          currentPhoto={profile.photo_apres}
          pid={profile.id}
          type="apres"
          onUpdate={onUpdate}
        />
      </div>
      {/* My measures */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <MeasureBox label="Depart (J0)" poids={profile.poids_j0} mg={profile.mg_j0} mm={profile.mm_j0} variant="muted" />
        <MeasureBox label="Actuel" poids={profile.derniere_mesure?.poids} mg={profile.derniere_mesure?.mg} mm={profile.derniere_mesure?.mm} variant="accent" />
      </div>
    </div>
  );
}

function PhotoUploader({ label, currentPhoto, pid, type, onUpdate }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      // Compress for Android/mobile compatibility
      const compressed = await new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width, h = img.height;
          const maxW = 1200;
          if (w > maxW) { h = (h * maxW) / w; w = maxW; }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.75));
        };
        img.onerror = () => resolve(null);
        img.src = URL.createObjectURL(file);
      });
      const photo = compressed || await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(file);
      });
      await apiClient.post(`/challenge/participants/${pid}/photo-${type}`, { photo });
      toast.success(`Photo ${type} ajoutee !`);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
    finally { setUploading(false); }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/challenge/participants/${pid}/photo-${type}`);
      toast.success('Photo supprimee');
      onUpdate();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div>
      <p className="text-[10px] text-[#AAAAAA] uppercase tracking-wider font-semibold mb-1.5">{label}</p>
      {currentPhoto ? (
        <div className="relative rounded-lg overflow-hidden border border-[#2A2A2E] aspect-[3/4]">
          <img src={currentPhoto} alt={label} className="w-full h-full object-cover" />
          <button onClick={handleDelete}
            className="absolute top-1.5 right-1.5 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center text-red-400 hover:text-red-300">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-full aspect-[3/4] bg-[#0F0F0F] border-2 border-dashed border-[#2A2A2E] rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#FF6B35]/40 transition-colors"
          data-testid={`upload-photo-${type}`}>
          {uploading ? (
            <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Camera className="w-6 h-6 text-zinc-600" />
              <span className="text-xs text-zinc-500">Ajouter</span>
            </>
          )}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

function TransformationCard({ t, isAdmin, onUpdate }) {
  const hasBoth = t.photo_avant && t.photo_apres;
  const deltaP = t.poids_j0 && t.poids_actuel ? (t.poids_actuel - t.poids_j0) : null;
  const deltaMG = t.mg_j0 && t.mg_actuel ? (t.mg_actuel - t.mg_j0) : null;
  const deltaMM = t.mm_j0 && t.mm_actuel ? (t.mm_actuel - t.mm_j0) : null;

  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg overflow-hidden" data-testid={`transfo-card-${t.id}`}>
      {/* Name header */}
      <div className="px-4 py-2.5 border-b border-[#2A2A2E] flex items-center gap-2">
        <UserAvatar name={t.nom} photoPath={t.profile_photo} size="sm" isChallenger />
        <span className="text-sm font-bold text-white flex-1">{t.nom}</span>
        <span className="text-[10px] text-[#AAAAAA]">{t.objectif === 'gras' ? 'Perte gras' : 'Prise muscle'}</span>
      </div>

      {/* Photos side by side */}
      <div className="grid grid-cols-2 relative">
        <div className="relative">
          <p className="absolute top-2 left-2 z-10 text-[9px] font-bold text-white bg-black/60 px-2 py-0.5 rounded">AVANT</p>
          {t.photo_avant ? (
            <img src={t.photo_avant} alt="Avant" className="w-full aspect-[3/4] object-cover" />
          ) : (
            <div className="w-full aspect-[3/4] bg-[#0F0F0F] flex items-center justify-center">
              <span className="text-xs text-zinc-700">Pas de photo</span>
            </div>
          )}
        </div>
        <div className="relative">
          <p className="absolute top-2 right-2 z-10 text-[9px] font-bold text-white bg-[#FF6B35]/80 px-2 py-0.5 rounded">APRES</p>
          {t.photo_apres ? (
            <img src={t.photo_apres} alt="Apres" className="w-full aspect-[3/4] object-cover" />
          ) : (
            <div className="w-full aspect-[3/4] bg-[#0F0F0F] flex items-center justify-center">
              <span className="text-xs text-zinc-700">Pas de photo</span>
            </div>
          )}
        </div>
        {hasBoth && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#FF6B35] flex items-center justify-center z-10 shadow-lg">
            <ArrowRight className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      {/* Measures comparison */}
      {(t.poids_j0 || t.poids_actuel) && (
        <div className="px-4 py-3 border-t border-[#2A2A2E]">
          <div className="grid grid-cols-3 gap-2 text-center">
            <DeltaMetric label="Poids" before={t.poids_j0} after={t.poids_actuel} unit="kg" delta={deltaP} lower />
            <DeltaMetric label="Gras" before={t.mg_j0} after={t.mg_actuel} unit="%" delta={deltaMG} lower />
            <DeltaMetric label="Muscle" before={t.mm_j0} after={t.mm_actuel} unit="kg" delta={deltaMM} />
          </div>
        </div>
      )}
    </div>
  );
}

function DeltaMetric({ label, before, after, unit, delta, lower }) {
  const good = delta != null ? (lower ? delta < 0 : delta > 0) : false;
  return (
    <div>
      <p className="text-[9px] text-[#AAAAAA] uppercase mb-0.5">{label}</p>
      <div className="flex items-center justify-center gap-1 text-xs">
        <span className="text-zinc-500 font-mono">{before ?? '-'}</span>
        <ArrowRight className="w-3 h-3 text-zinc-600" />
        <span className="text-white font-mono font-bold">{after ?? '-'}</span>
      </div>
      {delta != null && (
        <p className={`text-[10px] font-mono font-bold mt-0.5 ${good ? 'text-[#2e7d32]' : 'text-[#c0392b]'}`}>
          {delta > 0 ? '+' : ''}{delta.toFixed(1)}{unit}
        </p>
      )}
    </div>
  );
}

function MeasureBox({ label, poids, mg, mm, variant }) {
  const color = variant === 'accent' ? 'text-[#FF6B35]' : 'text-[#AAAAAA]';
  const bg = variant === 'accent' ? 'bg-[#FF6B35]/10 border-[#FF6B35]/20' : 'bg-[#0F0F0F] border-[#2A2A2E]';
  return (
    <div className={`${bg} border rounded-md p-2.5 text-center`}>
      <p className={`text-[10px] ${color} uppercase font-semibold mb-1`}>{label}</p>
      <p className="font-mono text-xs text-white">{poids ?? '-'} kg</p>
      <p className="font-mono text-[10px] text-zinc-400">{mg ?? '-'}% · {mm ?? '-'} kg</p>
    </div>
  );
}
